import React from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { formatDate } from '../../../../helper/formatDate';
import { getWhatsAppAppUrl, getWhatsAppWebUrl, getServiceBadgeClass } from './enquiryUtils';
import { SchoolInfoCell } from './SchoolInfoCell';
import { EnquiryMobileCard } from './EnquiryMobileCard';
import { UpateEnquiryModal } from '../UpateEnquiryModal';

export const getEnquiryColumns = ({
    authData,
    enquirys,
    handleEnquiryDelete,
    handleReport
}) => [
        {
            Header: 'School Details',
            accessor: 'name',
            HeaderClass: 'text-start',
            DataClass: 'text-start align-middle',
            Cell: (cell) => <SchoolInfoCell row={cell.row.original} />
        },
        {
            Header: 'Phone / Principal',
            accessor: 'phone',
            HeaderClass: 'text-start',
            DataClass: 'align-middle text-start',
            Cell: (cell) => {
                const row = cell.row.original;
                return (
                    <div>
                        {row.father_name && (
                            <span
                                className="fw-semibold text-dark fs-12 d-block text-truncate"
                                title={`Principal: ${row.father_name}`}
                            >
                                <i className="ri-user-star-line text-muted me-1" />
                                {row.father_name}
                            </span>
                        )}
                        <a
                            href={`tel:${row.phone}`}
                            className="text-success fw-medium text-decoration-none d-inline-flex align-items-center mt-0.5"
                        >
                            <i className="bx bx-phone me-1 text-success fs-13" />
                            {row.phone}
                        </a>
                    </div>
                );
            }
        },
        {
            Header: 'Date & Enq By',
            accessor: 'created_at',
            HeaderClass: 'text-start',
            DataClass: 'align-middle text-start',
            Cell: (cell) => {
                const row = cell.row.original;
                const user = row.created_by;
                const name = user ? `${user.first_name} ${user.last_name || ''}`.trim() : 'System';
                return (
                    <div>
                        <span className="fw-bold text-dark fs-12 d-block">
                            {formatDate(row.created_at)}
                        </span>
                        <small className="text-muted fs-11">
                            <i className="ri-user-line me-1" />
                            {name}
                        </small>
                    </div>
                );
            }
        },
        {
            Header: 'Preferred Service',
            accessor: 'course.name',
            HeaderClass: 'text-center',
            DataClass: 'text-center align-middle',
            Cell: (cell) => {
                const serviceName = cell.value;
                const badgeClass = getServiceBadgeClass(serviceName);
                return (
                    <span className={`service-pill-badge ${badgeClass}`}>
                        {serviceName || 'N/A'}
                    </span>
                );
            }
        },
        {
            Header: 'Report',
            HeaderClass: authData?.role?.priority >= 50 ? 'text-center' : 'd-none',
            DataClass: authData?.role?.priority >= 50 ? 'text-center align-middle' : 'd-none',
            Cell: (cell) => (
                <button
                    className="btn btn-sm btn-soft-info waves-effect py-1 px-2 fs-11 d-inline-flex align-items-center"
                    onClick={() => handleReport(cell.row.original)}
                >
                    <i className="bx bx-history me-1" />
                    Report
                </button>
            )
        },
        {
            Header: 'Action',
            HeaderClass: 'text-center',
            DataClass: 'text-center align-middle',
            Cell: (cell) => {
                const row = cell.row.original;
                const appUrl = getWhatsAppAppUrl(row.whatsapp, row.phone, row.name);
                const webUrl = getWhatsAppWebUrl(row.whatsapp, row.phone, row.name);
                const hasPhone = Boolean(appUrl || webUrl);

                return (
                    <div className="d-flex align-items-center justify-content-center gap-1">
                        {/* WhatsApp Action Button with App and Web Options */}
                        {hasPhone ? (
                            <Dropdown align="end" className="d-inline-block">
                                <Dropdown.Toggle
                                    variant="soft-success"
                                    size="sm"
                                    className="py-1 px-2 border-0 arrow-none d-inline-flex align-items-center"
                                    title="Chat on WhatsApp"
                                >
                                    <i className="ri-whatsapp-line fs-14 text-success" />
                                </Dropdown.Toggle>

                                <Dropdown.Menu className="shadow border-0 py-1" style={{ minWidth: '185px', zIndex: 1050 }}>
                                    <Dropdown.Item
                                        href={appUrl}
                                        className="d-flex align-items-center gap-2 py-1.5 fs-12 text-dark"
                                    >
                                        <i className="ri-smartphone-line text-success fs-15" />
                                        <div>
                                            <span className="fw-semibold d-block">WhatsApp App</span>
                                            <small className="text-muted fs-11">Open Desktop / Phone App</small>
                                        </div>
                                    </Dropdown.Item>

                                    <Dropdown.Divider className="my-1" />

                                    <Dropdown.Item
                                        href={webUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="d-flex align-items-center gap-2 py-1.5 fs-12 text-dark"
                                    >
                                        <i className="ri-global-line text-primary fs-15" />
                                        <div>
                                            <span className="fw-semibold d-block">WhatsApp Web</span>
                                            <small className="text-muted fs-11">Open in Browser</small>
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        ) : (
                            <button
                                type="button"
                                disabled
                                className="btn btn-sm btn-light py-1 px-2 opacity-50"
                                title="No WhatsApp/Phone number available"
                            >
                                <i className="ri-whatsapp-line fs-14" />
                            </button>
                        )}

                        <UpateEnquiryModal data={row} apiHandler={enquirys} />

                        <Button
                            onClick={() => handleEnquiryDelete(row.id)}
                            className="btn btn-sm btn-soft-danger py-1 px-2"
                            title="Delete Enquiry"
                        >
                            <i className="ri-delete-bin-fill fs-12" />
                        </Button>
                    </div>
                );
            }
        },
        {
            Header: 'List',
            HeaderClass: 'd-none',
            DataClass: 'd-none',
            listClass: 'list-group-item border-0 px-0 bg-transparent',
            list: (row) => (
                <EnquiryMobileCard
                    row={row}
                    enquirys={enquirys}
                    authData={authData}
                    handleEnquiryDelete={handleEnquiryDelete}
                    handleReport={handleReport}
                />
            )
        }
    ];

export default getEnquiryColumns;
